import React, { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';

interface CodeBoxProps {
  title?: string;
}

function getBrandingName(): string {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("quickstartapi.com")) return "QuickStartAPI";
  }
  return "MyInstantAPI";
}

function getDomainURL(): string {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  if (hostname.includes("quickstartapi.com")) return "https://quickStartAPI.com";
  return "https://myInstantapi.com";
}


const compactCode = {
  curl: `curl -X POST ${getDomainURL()} 
  --data '{
  "openapispec"&#58; {
    "openapi"&#58; "3.0.0",
    "info"&#58; {
      "title"&#58; "HelloWorld API",
      "version"&#58; "1.0.0" },
    "paths"&#58; {
      "/helloWorld"&#58; {
        "get"&#58; {
          "responses"&#58; {
            "200"&#58; {
              <span class="text-purple-400">"description"</span>: "Returns a greeting",
              "content"&#58; {
                "text/plain"&#58; {
                  "schema"&#58; {
                    "type"&#58; "string" }}}}}}}},
  }}'`,
  prompt: `curl -X POST ${getDomainURL()} 
  --data '{
  "prompt"&#58; 
     "Create an API with a helloWorld resource 
     and a GET method that returns Hello World",
  <span class="text-purple-400">"target"</span>:<span class="text-blue-400">"https://mybackend.com"</span>,
  }}'`,
  autogenerate: `curl -X POST ${getDomainURL()} 
  --data '{
    "target"&#58; "https://my.backend.com"
  }'

  <span class="text-blue-400"># this will generate a proxy for your backend that</span>
  <span class="text-blue-400"># automatically updates its security and dev portal</span>
  <span class="text-blue-400"># based on the traffic</span>`,
  curlTerminal: `curl -X POST ${getDomainURL()}  \
  --data '{\
  "openapispec": {\
    "openapi": "3.0.0",\
    "info": {\
      "title": "HelloWorld API",\
      "version": "1.0.0" },\
    "paths": {\
      "/helloWorld": {\
        "get": {\
          "responses": {\
            "200": {\
              "description": "Returns Hello World",\
              "content": {\
                "text/plain": {\
                  "schema": {\
                    "type": "string" }}}}}}}},\
  "adminEmail":""}'`,
  promptTerminal: `curl -X POST ${getDomainURL()}  \
  --data '{ "prompt": "Create an API with a helloWorld resource and a GET method that returns Hello World","adminEmail":""}'`
};

const compactCodeToCopy = {
  curl: `curl -X POST ${getDomainURL()}  
  --data '{
  "openapispec": {
    "openapi": "3.0.0",
    "info": {
      "title": "HelloWorld API",
      "version": "1.0.0" },
    "paths": {
      "/helloWorld": {
        "get": {
          "responses": {
            "200": {
              "description": "Returns a greeting",
              "content": {
                "text/plain": {
                  "schema": {
                    "type": "string" }}}}}}}},
  "adminEmail":"[insert email there]"}}'`,
  prompt: `curl -X POST ${getDomainURL()} 
  --data '{
  "prompt": 
     "Create an API with a helloWorld resource 
     and a GET method that returns Hello World",
  "target":"https://pokeapi.co/api/v2/pokemon",
  "adminEmail":"[insert email there]"}}'`,
  autogenerate: `curl -X POST ${getDomainURL()} \
  --data '{
    "target": "https://pokeapi.co/api/v2/pokemon"
  }'`,
  curlTerminal: `curl -X POST ${getDomainURL()} \
  --data '{\
  "openapispec": {\
    "openapi": "3.0.0",\
    "info": {\
      "title": "HelloWorld API",\
      "version": "1.0.0" },\
    "paths": {\
      "/helloWorld": {\
        "get": {\
          "responses": {\
            "200": {\
              "description": "Returns Hello World",\
              "content": {\
                "text/plain": {\
                  "schema": {\
                    "type": "string" }}}}}}}},\
  "adminEmail":""}'`,
  promptTerminal: `curl -X POST ${getDomainURL()} \
  --data '{ "prompt": "Create an API with a helloWorld resource and a GET method that returns Hello World","adminEmail":""}'`
};

const CodeBox: React.FC<CodeBoxProps> = ({ title = "Try it out now!" }) => {
  const [activeTab, setActiveTab] = useState<'prompt' | 'curl' | 'autogenerate'>('autogenerate');
  const [activeTabToCopy, setActiveTabToCopy] = useState<'prompt' | 'curl' | 'autogenerate'>('autogenerate');
  const [copied, setCopied] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState(1000);
  const [brandName, setBrandName] = useState("MyInstantAPI");


  useEffect(() => {
    setBrandName(getBrandingName());
    const logScreenSize = () => {
      // Change breakpoint to 700px to match your current width
      setScreenWidth(window.innerWidth);
    }
    
    // Run on mount
    logScreenSize();
    
    // Add resize listener
    window.addEventListener('resize', logScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', logScreenSize);
  }, [])

  const copyToClipboard = async (): Promise<void> => {
    try {
      const codeMap = {
        prompt: compactCodeToCopy.promptTerminal,
        curl: compactCodeToCopy.curlTerminal,
        autogenerate: compactCodeToCopy.autogenerate
      };
      await navigator.clipboard.writeText(codeMap[activeTabToCopy]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatCode = (code: string): string => {
    return code
      .replace(/(curl -X POST)/g, '<span class="text-blue-400">$1</span>')
      .replace(/(--data)/g, '<span class="text-blue-400">$1</span>')
      .replace(/(https:\/\/[^\s]+)/g, '<span class="text-green-400">$1</span>')
      .replace(/("[\w\s]+")&#58;/g, '<span class="text-purple-400">$1:</span>')
      .replace(/:\s*("[\w\s./-\\]+")[,}]/g, ': <span class="text-yellow-400">$1</span>')
      .replace(/\n/g, '<br>')
      .replace(/\s{2}/g, '&nbsp;&nbsp;');
  };

  return (
    <div className="mt-8 relative">
      <div className="flex items-center gap-4">
        <div className="w-3/5">
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm text-slate-500">{title}</h3>
            </div>
            
            <div className="flex text-sm">
              <button
                onClick={() => {setActiveTab('autogenerate'); setActiveTabToCopy('autogenerate')}}
                className={`px-4 py-2 rounded-t-lg ${
                  activeTab === 'autogenerate'
                    ? 'bg-[#0A0C10] text-slate-200 border border-b-0 border-slate-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Autogenerate
              </button>
              <button
                onClick={() => {setActiveTab('prompt'); setActiveTabToCopy('prompt')}}
                className={`px-4 py-2 rounded-t-lg ${
                  activeTab === 'prompt'
                    ? 'bg-[#0A0C10] text-slate-200 border border-b-0 border-slate-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Prompt
              </button>
              <button
                onClick={() => {setActiveTab('curl'); setActiveTabToCopy('curl')}}
                className={`px-4 py-2 rounded-t-lg ${
                  activeTab === 'curl'
                    ? 'bg-[#0A0C10] text-slate-200 border border-b-0 border-slate-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                OpenAPI
              </button>
            </div>
          </div>
          
          <div className="relative">
            <pre className="bg-[#0A0C10] rounded-tr-lg rounded-b-lg p-4 text-xs overflow-x-auto h-80">
              <code
                className="font-mono text-slate-200 whitespace-pre"
                dangerouslySetInnerHTML={{
                  __html: formatCode(compactCode[activeTab])
                }}
              />
            </pre>
            <button
              onClick={copyToClipboard}
              className="absolute top-2 right-2 p-1 text-slate-500 hover:text-slate-300 transition-colors"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-400" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        
        {screenWidth > 1000 && (
          <>
            <div className="flex-none px-2 translate-y-3">
              <span className="text-4xl block text-[100px] font-black contrast-200 brightness-0">
                ➜
              </span>
            </div>
            
            <div className="w-1/2 flex flex-col items-center translate-y-6">
              <div className="text-sm text-slate-900 font-mono space-y-1 text-center">
                <p className="text-xl contrast-200 brightness-0">
                  curl https://myAPI.myInstantAPI.com/helloWorld
                </p>
                <p className="text-xl contrast-200 brightness-0">
                  -H &#123;x-api-key&quot;:&quot;••••••••&quot;&#125;
                </p>
                <p className="mt-8 text-3xl font-black">
                  <b>200 OK &quot;Hello World!&quot; 🎉</b>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CodeBox;